<template>
  <div class="tpp-page">
    <nearby-housekeeping-ops-nav active-path="/pages/tpp/nearby-housekeeping/orders" :platform-id="platformId" />
    <section class="tpp-panel" v-loading="loading">
      <div class="tpp-panel__head">
        <div><h2 class="tpp-panel__title">订单详情</h2><p class="tpp-muted">{{ order.orderId || '-' }}</p></div>
        <div>
          <el-button size="small" @click="load">刷新</el-button>
          <el-button size="small" @click="goNearby('/pages/tpp/nearby-housekeeping/orders')">返回列表</el-button>
        </div>
      </div>
      <el-descriptions :column="3" border size="small">
        <el-descriptions-item label="本地订单">{{ order.orderId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="商户单号">{{ order.merchantOrderNo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="本地状态">{{ order.orderStatusText || '-' }}<span class="tpp-muted"> {{ order.orderStatus }}</span></el-descriptions-item>
        <el-descriptions-item label="金额">{{ order.amount || '-' }}</el-descriptions-item>
        <el-descriptions-item label="用户">{{ order.userId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="小区">{{ order.communityId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ order.createTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="众合订单号">{{ order.externalOrderId || '未建单' }}</el-descriptions-item>
        <el-descriptions-item label="说明"><span class="tpp-muted">众合接口状态仅后台可见，用户端只展示本地订单链路</span></el-descriptions-item>
      </el-descriptions>
      <div style="margin-top:12px"><el-button size="small" type="primary" @click="createRefund">登记退款申请</el-button></div>
    </section>

    <section class="tpp-panel">
      <h3 class="tpp-panel__title">订单流转</h3>
      <el-alert v-if="failedStages.length" type="error" :closable="false" show-icon style="margin-bottom:12px"
        :title="`存在需人工处理的环节：${failedStages.map(item => item.title).join('、')}`" />
      <el-table :data="stages" border size="small">
        <el-table-column prop="title" label="环节" width="200" />
        <el-table-column label="状态" width="120">
          <template slot-scope="scope"><el-tag size="small" :type="stageTag(scope.row.status).type">{{ stageTag(scope.row.status).label }}</el-tag></template>
        </el-table-column>
        <el-table-column prop="detail" label="说明" min-width="360" />
      </el-table>
    </section>

    <section class="tpp-panel">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="流转时间线" name="timeline">
          <el-radio-group v-model="category" size="small" style="margin-bottom:12px">
            <el-radio-button v-for="item in categories" :key="item.value" :label="item.value">{{ item.label }}</el-radio-button>
          </el-radio-group>
          <el-table :data="filteredTimeline" border size="small">
            <el-table-column prop="occurredAt" label="时间" width="170" />
            <el-table-column label="分类" width="90"><template slot-scope="scope">{{ categoryText(scope.row.category) }}</template></el-table-column>
            <el-table-column label="事件" min-width="200">
              <template slot-scope="scope">{{ scope.row.title }}<div class="tpp-muted">{{ scope.row.eventType }}</div></template>
            </el-table-column>
            <el-table-column prop="sourceText" label="来源" width="100" />
            <el-table-column label="用户可见" width="90">
              <template slot-scope="scope"><el-tag size="mini" :type="scope.row.userVisible ? 'success' : 'info'">{{ scope.row.userVisible ? '是' : '否' }}</el-tag></template>
            </el-table-column>
            <el-table-column prop="summary" label="摘要" min-width="220" show-overflow-tooltip />
            <el-table-column label="原始报文" width="100">
              <template slot-scope="scope">
                <el-popover v-if="scope.row.payloadJson" placement="left" width="520" trigger="click">
                  <pre style="max-height:360px;overflow:auto;white-space:pre-wrap;word-break:break-all">{{ pretty(scope.row.payloadJson) }}</pre>
                  <el-button slot="reference" type="text">查看</el-button>
                </el-popover>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="支付单" name="payment">
          <el-descriptions v-if="payment" :column="2" border size="small">
            <el-descriptions-item label="支付单号">{{ payment.paymentId }}</el-descriptions-item>
            <el-descriptions-item label="状态">{{ payment.status }}</el-descriptions-item>
            <el-descriptions-item label="金额（分）">{{ payment.payAmountFen }}</el-descriptions-item>
            <el-descriptions-item label="微信流水">{{ payment.transactionId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="收款池">{{ payment.poolId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="收款商户">{{ payment.providerMchId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="服务商商户">{{ payment.serviceMchId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="到账时间">{{ formatMillis(payment.verifiedAt) }}</el-descriptions-item>
          </el-descriptions>
          <p v-else class="tpp-muted">暂无本平台支付单</p>
        </el-tab-pane>
        <el-tab-pane label="众合推送记录" name="outbox">
          <el-table :data="outboxes" border size="small">
            <el-table-column prop="messageId" label="记录ID" min-width="160" />
            <el-table-column prop="operation" label="操作" width="140" />
            <el-table-column prop="messageStatus" label="状态" width="110" />
            <el-table-column prop="retryCount" label="失败次数" width="90" />
            <el-table-column prop="nextRetryAt" label="下次重试" width="170" />
            <el-table-column prop="updateTime" label="更新时间" width="170" />
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="退款记录" name="refunds">
          <el-table :data="refunds" border size="small">
            <el-table-column prop="refundId" label="退款单" min-width="150" />
            <el-table-column prop="refundAmountFen" label="金额（分）" width="100" />
            <el-table-column label="退款状态" width="120"><template slot-scope="scope"><el-tag size="small" :type="refundStatus(scope.row.refundStatus).type">{{ refundStatus(scope.row.refundStatus).label }}</el-tag></template></el-table-column>
            <el-table-column label="三方审核" width="130"><template slot-scope="scope"><el-tag size="small" :type="providerStatus(scope.row.providerStatus).type">{{ providerStatus(scope.row.providerStatus).label }}</el-tag></template></el-table-column>
            <el-table-column label="渠道状态" width="120"><template slot-scope="scope"><el-tag size="small" :type="channelStatus(scope.row.channelStatus).type">{{ channelStatus(scope.row.channelStatus).label }}</el-tag></template></el-table-column>
            <el-table-column prop="rejectReason" label="原因/备注" min-width="200" show-overflow-tooltip />
            <el-table-column prop="updateTime" label="更新时间" width="170" />
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </section>
  </div>
</template>
<script>
import NearbyHousekeepingOpsNav from './nearbyHousekeepingNav.vue'
import { nearbyHousekeepingOpsMixin } from './nearbyHousekeepingOpsMixin'
import { getNearbyHousekeepingOrderFlow, nhPayload, saveNearbyHousekeepingRefund } from '@/api/tpp/nearbyHousekeepingOpsApi'
import { providerStatus, channelStatus, refundStatus } from './nearbyHousekeepingRefundStatus'
import '../tpp.scss'

const STAGE_TAGS = {
  DONE: { label: '完成', type: 'success' },
  PROCESSING: { label: '进行中', type: '' },
  WAITING: { label: '等待中', type: 'warning' },
  RETRYING: { label: '重试中', type: 'warning' },
  FAILED: { label: '异常待处理', type: 'danger' },
  NOT_STARTED: { label: '未开始', type: 'info' },
  SKIPPED: { label: '不适用', type: 'info' }
}
const CATEGORIES = [
  { value: 'ALL', label: '全部' },
  { value: 'LOCAL', label: '本地订单' },
  { value: 'PAYMENT', label: '支付' },
  { value: 'SUPPLIER', label: '众合接口' },
  { value: 'AFTERSALE', label: '取消/退款' }
]

export default {
  name: 'NearbyHousekeepingOrderDetail',
  components: { NearbyHousekeepingOpsNav },
  mixins: [nearbyHousekeepingOpsMixin],
  data() {
    return { loading: false, order: {}, stages: [], timeline: [], payment: null, outboxes: [], refunds: [], activeTab: 'timeline', category: 'ALL', categories: CATEGORIES }
  },
  computed: {
    failedStages() { return this.stages.filter(item => item.status === 'FAILED') },
    filteredTimeline() { return this.category === 'ALL' ? this.timeline : this.timeline.filter(item => item.category === this.category) }
  },
  async created() { if (await this.ensureNearbyHousekeepingPlatform()) this.load() },
  methods: {
    providerStatus, channelStatus, refundStatus,
    async load() {
      const orderId = String(this.$route.query.orderId || '')
      if (!orderId) return
      this.loading = true
      try {
        const flow = nhPayload(await getNearbyHousekeepingOrderFlow({ platformId: this.platformId, orderId })) || {}
        this.order = flow.order || {}
        this.stages = flow.stages || []
        this.timeline = flow.timeline || []
        this.payment = flow.payment || null
        this.outboxes = flow.outboxes || []
        this.refunds = flow.refunds || []
      } catch (e) { this.$message.error(e.message || '订单详情加载失败') }
      finally { this.loading = false }
    },
    stageTag(status) { return STAGE_TAGS[status] || { label: status || '-', type: 'info' } },
    categoryText(value) { const item = CATEGORIES.find(c => c.value === value); return item ? item.label : value },
    pretty(json) { try { return JSON.stringify(JSON.parse(json), null, 2) } catch (e) { return json } },
    formatMillis(value) {
      const n = Number(value)
      if (!n) return '-'
      const d = new Date(n)
      const pad = v => String(v).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    async createRefund() {
      try {
        const amount = await this.$prompt('退款金额（分），由客服按已支付金额填写', '登记退款', { inputPattern: /^[1-9]\d*$/, inputErrorMessage: '请输入正整数分' })
        const reason = await this.$prompt('退款原因', '登记退款')
        await saveNearbyHousekeepingRefund({ platformId: this.platformId, orderId: this.order.orderId, refundAmountFen: amount.value, reason: reason.value })
        this.$message.success('已登记退款申请')
        this.goNearby('/pages/tpp/nearby-housekeeping/refunds')
      } catch (e) { if (e !== 'cancel') this.$message.error(e.message || '登记失败') }
    }
  }
}
</script>
